import Input from "@/components/common/input/Input";
import Breadcrumb from "@/components/bread-crumb/BreadCrumb";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import "./UpdateUnderwriterUtility.css";

import type {
  PolicySearchCriteria,
  PolicySearchResponse,
  SubCompany,
  Underwriter,
  UnderwriterDetails,
  UnderwriterNameOperator,
  PolicyTypes,
  PolicyState,
  ProcessStatuses,
  TransactionType,
  PolicyTransactionStatus
} from "@/models/underwriter";

import { underwriterService } from "@/services/underwriterService";

const operatorOptions: { label: string; value: UnderwriterNameOperator }[] = [
  { label: "Contains", value: "CONTAINS" },
  { label: "Does Not Contain", value: "NOT_CONTAINS" },
  { label: "Begins With", value: "BEGINS_WITH" },
  { label: "Equals", value: "EQUALS" }
];

function asArrayMaybe(v: any): any[] {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function pickString(x: any, keys: string[]): string {
  for (const k of keys) {
    const v = x?.[k];
    if (typeof v === "string") {
      const t = v.trim();
      if (t.length > 0) return t;
    }
  }
  return "";
}

function pickNumber(x: any, keys: string[], fallback = 0): number {
  for (const k of keys) {
    const v = x?.[k];
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function normalizeSubCompanyList(list: any[]): SubCompany[] {
  return asArrayMaybe(list).map((x) => {
    const code = pickString(x, ["SubCompanyCode", "subCompanyCode", "sub_company_code", "code", "Code"]);
    const name = pickString(x, ["SubCompanyName", "subCompanyName", "sub_company_name", "name", "Name"]);
    const disp = pickString(x, ["SubCompanyDisplayName", "subCompanyDisplayName", "displayName", "DisplayName", "display_name"]);
    return {
      SubCompanyCode: code || null,
      SubCompanyName: name || null,
      SubCompanyDisplayName: disp || null
    };
  });
}

function normalizePolicyStateList(list: any[]): PolicyState[] {
  return asArrayMaybe(list).map((x) => {
    const id = pickNumber(x, ["StateId", "stateId", "id", "Id"]);
    const name = pickString(x, ["StateName", "stateName", "name", "Name"]);
    const disp = pickString(x, ["StateDisplayName", "stateDisplayName", "displayName", "DisplayName"]);
    return {
      StateId: id,
      StateName: name || null,
      StateDisplayName: disp || null
    };
  });
}

function normalizeProcessStatusList(list: any[]): ProcessStatuses[] {
  return asArrayMaybe(list).map((x) => {
    const code = pickString(x, ["ProcessStatusCode", "processStatusCode", "code", "Code"]);
    const name = pickString(x, ["ProcessStatus", "processStatus", "name", "Name"]);
    const disp = pickString(x, ["ProcessStatusDisplayName", "processStatusDisplayName", "displayName", "DisplayName"]);
    return {
      ProcessStatusCode: code || null,
      ProcessStatus: name || null,
      ProcessStatusDisplayName: disp || null
    };
  });
}

function normalizeTxnTypeList(list: any[]): TransactionType[] {
  return asArrayMaybe(list).map((x) => {
    const code = pickString(x, ["TxnTypeCode", "txnTypeCode", "code", "Code"]);
    const name = pickString(x, ["TxnType", "txnType", "name", "Name"]);
    const disp = pickString(x, ["TransactionTypeDisplayName", "transactionTypeDisplayName", "displayName", "DisplayName"]);
    return {
      TxnTypeCode: code || null,
      TxnType: name || null,
      TransactionTypeDisplayName: disp || null
    };
  });
}

function normalizeTxnStatusList(list: any[]): PolicyTransactionStatus[] {
  return asArrayMaybe(list).map((x) => {
    const code = pickString(x, ["TxnStatusCode", "txnStatusCode", "code", "Code"]);
    const name = pickString(x, ["PolicyTxnStatus", "policyTxnStatus", "name", "Name"]);
    const disp = pickString(x, ["TransactionStatusDisplayName", "transactionStatusDisplayName", "displayName", "DisplayName"]);
    return {
      TxnStatusCode: code || null,
      PolicyTxnStatus: name || null,
      TransactionStatusDisplayName: disp || null
    };
  });
}

function normalizePolicyTypeList(list: any[]): PolicyTypes[] {
  return asArrayMaybe(list).map((x) => {
    const id = pickNumber(x, ["PolicyTypeId", "policyTypeId", "id", "Id"]);
    const name = pickString(x, ["PolicyType", "policyType", "name", "Name"]);
    const code = pickString(x, ["PolicyTypeCode", "policyTypeCode", "code", "Code"]);
    const sc = pickString(x, ["SubCompanyCode", "subCompanyCode"]);
    const disp = pickString(x, ["PolicyTypeDisplayName", "policyTypeDisplayName", "displayName", "DisplayName"]);
    return {
      PolicyTypeId: id,
      PolicyType: name || null,
      PolicyTypeCode: code || null,
      SubCompanyCode: sc || null,
      PolicyTypeDisplayName: disp || null
    };
  });
}

function normalizeUnderwriterList(list: any[]): Underwriter[] {
  return asArrayMaybe(list).map((x) => {
    const id = pickNumber(x, ["UserId", "userId", "userid", "id", "Id"]);
    const nm = pickString(x, ["UnderwriterName", "underwriterName", "underWriterName", "name", "displayName", "DisplayName"]);
    return {
      UserId: id,
      UnderwriterName: nm || null
    };
  });
}

function safeOptionText(...vals: Array<string | null | undefined>): string {
  for (const v of vals) {
    const s = String(v ?? "").trim();
    if (s.length > 0) return s;
  }
  return " ";
}

const UpdateUnderwriterUtility: FC = () => {
  const [operator, setOperator] = useState<UnderwriterNameOperator>("CONTAINS");
  const [underwriterText, setUnderwriterText] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [okMessage, setOkMessage] = useState<string | null>(null);

  const [underwriters, setUnderwriters] = useState<Underwriter[]>([]);
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalBusy, setModalBusy] = useState<boolean>(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const [subCompanies, setSubCompanies] = useState<SubCompany[]>([]);
  const [policyStates, setPolicyStates] = useState<PolicyState[]>([]);
  const [processStatuses, setProcessStatuses] = useState<ProcessStatuses[]>([]);
  const [transactionTypes, setTransactionTypes] = useState<TransactionType[]>([]);
  const [transactionStatuses, setTransactionStatuses] = useState<PolicyTransactionStatus[]>([]);

  const [policyTypes, setPolicyTypes] = useState<PolicyTypes[]>([]);
  const [destinationUnderwriters, setDestinationUnderwriters] = useState<Underwriter[]>([]);
  const [destinationId, setDestinationId] = useState<number>(0);

  const [criteria, setCriteria] = useState<PolicySearchCriteria>({
    SourceUnderwriterId: 0,
    SubCompanyCode: "",
    PolicyTypeId: 0,
    ProcessStatusCode: "",
    TransactionTypeCode: "",
    TransactionStatusCode: "",
    EffectiveDate: "",
    ExpieryDate: "",
    PolicyStateId: 0
  });

  const [policies, setPolicies] = useState<PolicySearchResponse[]>([]);
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<number[]>([]);
  const [policySearchAttempted, setPolicySearchAttempted] = useState<boolean>(false);

  const timersRef = useRef<number[]>([]);

  const selectedSource = useMemo(
    () => underwriters.find((u) => u.UserId === selectedSourceId) ?? null,
    [underwriters, selectedSourceId]
  );

  const isSubCompanyValid = (criteria.SubCompanyCode ?? "").trim().length > 0;
  const isPolicyStateValid = Number(criteria.PolicyStateId ?? 0) > 0;
  const isPolicyTypeValid = Number(criteria.PolicyTypeId ?? 0) > 0;
  const isDestinationValid = Number(destinationId ?? 0) > 0;
  const hasAtLeastOnePolicySelected = selectedPolicyIds.length > 0;

  const canSearchPolicies = isSubCompanyValid && isPolicyStateValid && isPolicyTypeValid && !modalBusy;
  const canConfirm =
    isSubCompanyValid &&
    isPolicyStateValid &&
    isPolicyTypeValid &&
    isDestinationValid &&
    policies.length > 0 &&
    hasAtLeastOnePolicySelected &&
    !modalBusy;

  const isAllSelected = policies.length > 0 && selectedPolicyIds.length === policies.length;

  useEffect(() => {
    void initLoad();
    return () => {
      timersRef.current.forEach((t) => window.clearTimeout(t));
      timersRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (modalOpen) document.body.classList.add("uw-no-scroll");
    else document.body.classList.remove("uw-no-scroll");
    return () => document.body.classList.remove("uw-no-scroll");
  }, [modalOpen]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && modalOpen && !modalBusy) closeModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalOpen, modalBusy]);

  function setTempOk(msg: string) {
    setOkMessage(msg);
    const t = window.setTimeout(() => setOkMessage(null), 2000);
    timersRef.current.push(t);
  }

  function setTempError(msg: string) {
    setError(msg);
    const t = window.setTimeout(() => setError(null), 2000);
    timersRef.current.push(t);
  }

  function setTempModalError(msg: string) {
    setModalError(msg);
    const t = window.setTimeout(() => setModalError(null), 2000);
    timersRef.current.push(t);
  }

  async function initLoad() {
    setLoading(true);
    setError(null);
    setOkMessage(null);
    setSelectionError(null);
    try {
      const [criteriaRes, uwRes] = await Promise.all([
        underwriterService.getPolicySearchCriteria(),
        underwriterService.searchSourceUnderwriters("CONTAINS", "")
      ]);

      const raw: any = criteriaRes.data ?? {};
      const scRaw = raw.SubCompanyList ?? raw.subCompanyList ?? raw.subCompany ?? raw.subCompanies ?? [];
      const stRaw = raw.PolicyStateList ?? raw.policyStateList ?? raw.states ?? raw.policyStates ?? [];
      const psRaw = raw.ProcessStatusList ?? raw.processStatusList ?? raw.processStatuses ?? [];
      const ttRaw = raw.TransactionTypeList ?? raw.transactionTypeList ?? raw.transactionTypes ?? [];
      const tsRaw = raw.PolicyTxnStatusList ?? raw.policyTxnStatusList ?? raw.txnStatuses ?? raw.transactionStatuses ?? [];

      setSubCompanies(normalizeSubCompanyList(scRaw));
      setPolicyStates(normalizePolicyStateList(stRaw));
      setProcessStatuses(normalizeProcessStatusList(psRaw));
      setTransactionTypes(normalizeTxnTypeList(ttRaw));
      setTransactionStatuses(normalizeTxnStatusList(tsRaw));

      setUnderwriters(uwRes.data ?? []);
    } catch (e: any) {
      setTempError(e?.message ?? "Failed to load underwriter data");
    } finally {
      setLoading(false);
    }
  }

  async function searchUnderwriters() {
    setLoading(true);
    setError(null);
    setOkMessage(null);
    setSelectionError(null);
    setSelectedSourceId(null);
    try {
      const res = await underwriterService.searchSourceUnderwriters(operator, underwriterText);
      const list = res.data ?? [];
      setUnderwriters(list);
      if (list.length === 0) setSelectionError("No underwriters found.");
    } catch (e: any) {
      setTempError(e?.message ?? "Failed to search underwriters");
      setUnderwriters([]);
      setSelectionError("No underwriters found.");
    } finally {
      setLoading(false);
    }
  }

  function openModal() {
    setError(null);
    setOkMessage(null);
    setModalError(null);
    setSelectionError(null);
    if (!selectedSourceId) {
      setSelectionError("Please select Underwriter");
      return;
    }
    setPolicies([]);
    setSelectedPolicyIds([]);
    setDestinationId(0);
    setDestinationUnderwriters([]);
    setPolicyTypes([]);
    setPolicySearchAttempted(false);
    setCriteria({
      SourceUnderwriterId: selectedSourceId,
      SubCompanyCode: "",
      PolicyTypeId: 0,
      ProcessStatusCode: "",
      TransactionTypeCode: "",
      TransactionStatusCode: "",
      EffectiveDate: "",
      ExpieryDate: "",
      PolicyStateId: 0
    });
    setModalOpen(true);
  }

  function closeModal() {
    if (modalBusy) return;
    setModalOpen(false);
    setModalError(null);
    setPolicies([]);
    setSelectedPolicyIds([]);
    setDestinationId(0);
    setDestinationUnderwriters([]);
    setPolicyTypes([]);
    setPolicySearchAttempted(false);
  }

  function updateCriteria<K extends keyof PolicySearchCriteria>(key: K, value: PolicySearchCriteria[K]) {
    setCriteria((prev) => ({ ...prev, [key]: value }));
  }

  function togglePolicy(id: number) {
    setSelectedPolicyIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function toggleSelectAll() {
    if (policies.length === 0) return;
    if (isAllSelected) setSelectedPolicyIds([]);
    else setSelectedPolicyIds(policies.map((p) => p.PolicyId));
  }

  async function onSubCompanyChanged(code: string) {
    updateCriteria("SubCompanyCode", code);
    updateCriteria("PolicyTypeId", 0);
    setModalError(null);
    setPolicies([]);
    setSelectedPolicyIds([]);
    setDestinationId(0);
    setDestinationUnderwriters([]);
    setPolicyTypes([]);
    setPolicySearchAttempted(false);
    const trimmed = String(code ?? "").trim();
    if (trimmed.length === 0) return;
    try {
      const res = await underwriterService.getUserAndPolicyType(trimmed);
      const raw: any = res.data ?? {};
      const uwRaw = raw.UnderwriterList ?? raw.underwriterList ?? raw.users ?? raw.userList ?? [];
      const ptRaw = raw.PolicyTypeList ?? raw.policyTypeList ?? raw.policyTypes ?? raw.policyType ?? [];
      const normalizedDest = normalizeUnderwriterList(uwRaw).filter((u) => u.UserId !== (selectedSourceId ?? 0));
      setDestinationUnderwriters(normalizedDest);
      setPolicyTypes(normalizePolicyTypeList(ptRaw));
      if (destinationId === (selectedSourceId ?? 0)) setDestinationId(0);
    } catch (e: any) {
      setTempModalError(e?.message ?? "Failed to load destination underwriters / policy types");
    }
  }

  async function onSearchPolicies() {
    setModalError(null);
    setPolicySearchAttempted(true);
    if (!isSubCompanyValid) {
      setTempModalError("Please select Sub Company");
      return;
    }
    if (!isPolicyTypeValid) {
      setTempModalError("Please select Policy Type");
      return;
    }
    if (!isPolicyStateValid) {
      setTempModalError("Please select Policy State");
      return;
    }
    setModalBusy(true);
    setPolicies([]);
    setSelectedPolicyIds([]);
    try {
      const res = await underwriterService.policySearch(criteria);
      const data = res.data ?? [];
      setPolicies(data);
      setSelectedPolicyIds([]);
      if (data.length === 0) setTempModalError("No records found.");
    } catch (e: any) {
      setTempModalError(e?.message ?? "Failed to search policies");
    } finally {
      setModalBusy(false);
    }
  }

  async function onConfirm() {
    setModalError(null);
    setError(null);
    setOkMessage(null);
    if (!isSubCompanyValid) {
      setTempModalError("Please select Sub Company");
      return;
    }
    if (!isPolicyTypeValid) {
      setTempModalError("Please select Policy Type");
      return;
    }
    if (!isPolicyStateValid) {
      setTempModalError("Please select Policy State");
      return;
    }
    if (!isDestinationValid) {
      setTempModalError("Please select Destination Underwriter");
      return;
    }
    if (policies.length === 0) {
      setTempModalError("No records found to confirm.");
      return;
    }
    if (selectedPolicyIds.length === 0) {
      setTempModalError("Please select at least one policy.");
      return;
    }
    if (!selectedSourceId) {
      setTempModalError("Please select Underwriter");
      return;
    }
    const payload: UnderwriterDetails = {
      SourceUnderwriterId: selectedSourceId,
      DestinationUnderwriterId: destinationId,
      SubCompanyCode: criteria.SubCompanyCode,
      PolicyTypeId: criteria.PolicyTypeId,
      PolicyIds: selectedPolicyIds
    };
    setModalBusy(true);
    try {
      const res = await underwriterService.confirmUpdate(payload);
      const resultText = String(res?.data ?? "").toLowerCase();
      if (res?.status === 200 && (resultText === "success" || resultText.includes("success"))) {
        setTempOk("Underwriter updated");
        closeModal();
      } else {
        setTempError("Update failed");
      }
    } catch (e: any) {
      setTempModalError(e?.message ?? "Failed to confirm update");
    } finally {
      setModalBusy(false);
    }
  }

  return (
    <>
      <article className="page-container">
        <div className="card-container">
          <Breadcrumb items={[{ label: "Home", path: "/" }, { label: "Update Underwriter", path: "/update-underwriter" }]} />

          <div className="card-header">
            <h2>Update Underwriter Utility</h2>
            <p className="subtitle">
              Search by <strong>Underwriter</strong>, then select source, get policies and confirm update.
            </p>
          </div>

          <div className="card-section">
            <h3 className="section-title">Search Underwriter</h3>

            <div className="uw-search-row">
              <div className="form-group">
                <label>Underwriter Name</label>
                <select className="uw-select" value={operator} onChange={(e) => setOperator(e.target.value as UnderwriterNameOperator)}>
                  {operatorOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Value</label>
                <Input
                  className="uw-input"
                  placeholder="Leave empty to load all"
                  value={underwriterText}
                  onChange={(e) => setUnderwriterText(e.target.value)}
                />
              </div>

              <button className="btn primary uw-search-btn" onClick={() => void searchUnderwriters()} disabled={loading}>
                {loading ? "Searching..." : "Search Underwriter to be updated"}
              </button>
            </div>

            {error ? <div className="err-text">{error}</div> : null}
            {okMessage ? <div className="ok-text">{okMessage}</div> : null}
          </div>

          <div className="card-section">
            <h3 className="section-title">Select Source Underwriter</h3>

            <div className="uw-getpolicies-center">
              <button className="btn secondary uw-get-btn" onClick={openModal}>
                Get Policies
              </button>
            </div>

            {selectionError ? <div className="err-text">{selectionError}</div> : null}

            {underwriters.length > 0 ? (
              <div className="uw-grid">
                <div className="uw-grid-header">
                  <div className="uw-grid-title">SOURCE Underwriter Name</div>
                  <div className="uw-count">{underwriters.length} records</div>
                </div>

                <div className="uw-grid-body">
                  {underwriters.map((u) => (
                    <div key={u.UserId} className="uw-grid-row">
                      <div className="uw-radio">
                        <input
                          type="radio"
                          name="sourceUW"
                          checked={selectedSourceId === u.UserId}
                          onChange={() => {
                            setSelectedSourceId(u.UserId);
                            setSelectionError(null);
                          }}
                        />
                      </div>
                      <div className="uw-name">{u.UnderwriterName ?? ""}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </article>

      {modalOpen ? (
        <div className="uw-modal-backdrop" role="dialog" aria-modal="true">
          <div className="uw-modal">
            <div className="uw-modal-header">
              <div className="uw-modal-title">Update Underwriter</div>
              <button className="uw-icon-btn" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>

            <div className="uw-modal-banner">Please select the Sub Company to search and make the updates</div>

            <div className="uw-modal-body">
              <div className="uw-source-line">
                <span className="uw-source-label">Source Underwriter:</span>
                <span className="uw-source-value">{selectedSource?.UnderwriterName ?? ""}</span>
              </div>

              <div className="uw-form-grid">
                <div className="uw-field">
                  <label>
                    Sub Company: <span className="uw-req">*</span>
                  </label>
                  <select className="uw-select" value={criteria.SubCompanyCode ?? ""} onChange={(e) => void onSubCompanyChanged(e.target.value)}>
                    <option value="">--Select--</option>
                    {subCompanies.map((s, idx) => {
                      const code = String(s.SubCompanyCode ?? "").trim();
                      const label = safeOptionText(s.SubCompanyDisplayName, s.SubCompanyName, s.SubCompanyCode);
                      return (
                        <option key={`${code || "sc"}-${idx}`} value={code}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="uw-field">
                  <label>
                    Policy Type <span className="uw-req">*</span>
                  </label>
                  <select className="uw-select" value={criteria.PolicyTypeId || 0} onChange={(e) => updateCriteria("PolicyTypeId", Number(e.target.value))}>
                    <option value={0}>--Select--</option>
                    {policyTypes.map((p, idx) => {
                      const label = safeOptionText(p.PolicyTypeDisplayName, p.PolicyType, p.PolicyTypeCode, String(p.PolicyTypeId));
                      return (
                        <option key={`${p.PolicyTypeId}-${idx}`} value={p.PolicyTypeId}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="uw-field">
                  <label>Process Status</label>
                  <select className="uw-select" value={criteria.ProcessStatusCode ?? ""} onChange={(e) => updateCriteria("ProcessStatusCode", e.target.value)}>
                    <option value="">--Select--</option>
                    {processStatuses.map((p, idx) => {
                      const code = String(p.ProcessStatusCode ?? "").trim();
                      const label = safeOptionText(p.ProcessStatusDisplayName, p.ProcessStatus, p.ProcessStatusCode);
                      return (
                        <option key={`${code || "ps"}-${idx}`} value={code}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="uw-field">
                  <label>Transaction Type</label>
                  <select className="uw-select" value={criteria.TransactionTypeCode ?? ""} onChange={(e) => updateCriteria("TransactionTypeCode", e.target.value)}>
                    <option value="">--Select--</option>
                    {transactionTypes.map((t, idx) => {
                      const code = String(t.TxnTypeCode ?? "").trim();
                      const label = safeOptionText(t.TransactionTypeDisplayName, t.TxnType, t.TxnTypeCode);
                      return (
                        <option key={`${code || "tt"}-${idx}`} value={code}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="uw-field">
                  <label>Transaction Status</label>
                  <select className="uw-select" value={criteria.TransactionStatusCode ?? ""} onChange={(e) => updateCriteria("TransactionStatusCode", e.target.value)}>
                    <option value="">--Select--</option>
                    {transactionStatuses.map((t, idx) => {
                      const code = String(t.TxnStatusCode ?? "").trim();
                      const label = safeOptionText(t.TransactionStatusDisplayName, t.PolicyTxnStatus, t.TxnStatusCode);
                      return (
                        <option key={`${code || "ts"}-${idx}`} value={code}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="uw-field">
                  <label>
                    Policy State <span className="uw-req">*</span>
                  </label>
                  <select className="uw-select" value={criteria.PolicyStateId || 0} onChange={(e) => updateCriteria("PolicyStateId", Number(e.target.value))}>
                    <option value={0}>--Select--</option>
                    {policyStates.map((s, idx) => {
                      const label = safeOptionText(s.StateDisplayName, s.StateName, String(s.StateId));
                      return (
                        <option key={`${s.StateId}-${idx}`} value={s.StateId}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="uw-field">
                  <label>Effective Date</label>
                  <input className="uw-input" type="date" value={criteria.EffectiveDate ?? ""} onChange={(e) => updateCriteria("EffectiveDate", e.target.value)} />
                </div>

                <div className="uw-field">
                  <label>Expiry Date</label>
                  <input className="uw-input" type="date" value={criteria.ExpieryDate ?? ""} onChange={(e) => updateCriteria("ExpieryDate", e.target.value)} />
                </div>

                <div className="uw-field">
                  <label>
                    Destination Underwriter <span className="uw-req">*</span>
                  </label>
                  <select className="uw-select" value={destinationId || 0} onChange={(e) => setDestinationId(Number(e.target.value))}>
                    <option value={0}>--Select--</option>
                    {destinationUnderwriters.map((u, idx) => {
                      const label = safeOptionText(u.UnderwriterName, String(u.UserId));
                      return (
                        <option key={`${u.UserId}-${idx}`} value={u.UserId}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {modalError ? <div className="err-text">{modalError}</div> : null}

              <div className="uw-modal-actions">
                <button className="btn primary" onClick={() => void onSearchPolicies()} disabled={!canSearchPolicies}>
                  {modalBusy ? "Searching..." : "Search"}
                </button>
              </div>

              {policySearchAttempted && !isSubCompanyValid ? (
                <div className="uw-table-wrap uw-table-disabled">
                  <div className="uw-table-head">
                    <div className="uw-table-title">Policies</div>
                  </div>
                  <div className="uw-table-disabled-msg">
                    Please select <b>Sub Company</b> to view the policy grid.
                  </div>
                </div>
              ) : null}

              {isSubCompanyValid && policies.length > 0 ? (
                <div className="uw-table-wrap">
                  <div className="uw-table-head">
                    <div className="uw-table-title">Policies ({policies.length})</div>
                    <label className="uw-selectall">
                      <input type="checkbox" checked={isAllSelected} onChange={toggleSelectAll} />
                      Select All
                    </label>
                  </div>

                  <div className="uw-table-scroll">
                    <table className="uw-table">
                      <thead>
                        <tr>
                          <th className="uw-col-check"></th>
                          <th>Policy #</th>
                          <th>Type</th>
                          <th>Txn</th>
                          <th>Term</th>
                          <th>Txn Status</th>
                          <th>Txn Type</th>
                          <th>Process</th>
                          <th>State</th>
                          <th>Insured</th>
                          <th>Agency</th>
                          <th>City</th>
                          <th>Zip</th>
                          <th>Eff</th>
                          <th>Exp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {policies.map((p) => (
                          <tr key={p.PolicyId}>
                            <td className="uw-col-check">
                              <input type="checkbox" checked={selectedPolicyIds.includes(p.PolicyId)} onChange={() => togglePolicy(p.PolicyId)} />
                            </td>
                            <td>{p.PolicyNum ?? ""}</td>
                            <td>{p.PolicyType ?? ""}</td>
                            <td>{p.PolicyTxnNum}</td>
                            <td>{p.PolicyTermNum}</td>
                            <td>{p.PolicyTxnStatus ?? ""}</td>
                            <td>{p.TxnType ?? ""}</td>
                            <td>{p.ProcessStatus ?? ""}</td>
                            <td>{p.StateName ?? ""}</td>
                            <td>{p.InsuredFullName ?? ""}</td>
                            <td>{p.AgencyFullName ?? ""}</td>
                            <td>{p.AgencyCity ?? ""}</td>
                            <td>{p.AgencyZipCode ?? ""}</td>
                            <td>{p.PolicyEff ?? ""}</td>
                            <td>{p.PolicyExp ?? ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="uw-modal-footer">
              <button className="btn secondary" onClick={() => void onConfirm()} disabled={!canConfirm}>
                {modalBusy ? "Confirming..." : "Confirm"}
              </button>
              <button className="btn tertiary" onClick={closeModal} disabled={modalBusy}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default UpdateUnderwriterUtility;